<template>
  <e-dialog
    ref="dialogRef"
    wide
    @open="handleDialogOpen"
    @close="handleDialogClose"
    @after-leave="handleDialogAfterLeave"
  >
    <template #title>
      <div class="caption">Групповая операция</div>
    </template>

    <template #close="{ closeDialog }">
      <div v-if="$vuetify.display.mdAndUp" class="d-flex">
        <e-btn variant="secondary" @click="closeDialog">Отмена</e-btn>
        <e-btn variant="primary" class="ml-4" :loading="isEditQuotaReservesUpdateLoading" @click="handleSave">
          Применить
        </e-btn>
      </div>
    </template>

    <template #buttons>
      <div v-if="!$vuetify.display.mdAndUp" class="d-flex justify-end w-100 mt-3">
        <e-btn variant="secondary" @click="dialogRef.close()">Отмена</e-btn>
        <e-btn variant="primary" class="ml-4" :loading="isEditQuotaReservesUpdateLoading" @click="handleSave">
          Применить
        </e-btn>
      </div>
    </template>

    <div class="paragraph-m_h text-secondary mb-6">
      Отметьте категории номеров квоту которых вы хотите отредактировать. <br />
      Также вы можете отредактировать период на котором надо внести изменения
    </div>

    <e-data-items v-model="selectedItems" :items="tableItems" :headers="tableHeaders" return-object>
      <template v-slot:[`item.name`]="{ item }">
        <div>
          {{ item.room.name[0].value }}
        </div>
      </template>
      <template v-slot:[`item.dataItemSecondRow`]="{ item }">
        <div
          v-if="isFormDirty && selectedItems.includes(item) && !+item.quota && !item.request && !item.close"
          class="d-flex align-center"
        >
          <e-icon name="alert" size="16" class="text-red mr-2" />
          <div class="text-red paragraph-xs font-400">
            Укажите квоту категории номера, выставьте её под запрос или закройте для продажи
          </div>
        </div>
      </template>

      <template v-slot:[`item.period.from`]="{ item }">
        <e-date-picker
          v-model="item.period.from"
          :min="chessPeriod.from"
          :max="chessPeriod.to"
          :disabled="!selectedItems.includes(item)"
        />
      </template>
      <template v-slot:[`item.period.to`]="{ item }">
        <e-date-picker
          v-model="item.period.to"
          :min="chessPeriod.from"
          :max="chessPeriod.to"
          :disabled="!selectedItems.includes(item)"
        />
      </template>
      <template v-slot:[`item.quota`]="{ item }">
        <e-input
          v-model="item.quota"
          :maska="quotaMask"
          :disabled="!selectedItems.includes(item)"
          @update:model-value="handleQuotaUpdate(item)"
        />
      </template>
      <template v-slot:[`item.request`]="{ item }">
        <e-checkbox
          v-model="item.request"
          :disabled="!selectedItems.includes(item)"
          @update:model-value="handleRequestUpdate(item)"
        />
      </template>
      <template v-slot:[`item.close`]="{ item }">
        <e-checkbox
          v-model="item.close"
          :disabled="!selectedItems.includes(item)"
          @update:model-value="handleCloseUpdate(item)"
        />
      </template>
    </e-data-items>
  </e-dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import { useDisplay } from 'vuetify';
import type { ChessDateCellType } from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';
import type { QuotaRoomType } from '@/parts/tariffs/store/quota';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, ECheckbox, EDatePicker, EIcon, EInput } from '@/core/components/ui';

import { quotaMask } from '@/core/consts/masks';
import { getDateWithUtcOffset, getIsoDateWithUtcOffset } from '@/core/utils/date';
import { QuotaReserveUpdateOperationKind } from '@/graphql/generated';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useQuotaStore } from '@/parts/tariffs/store/quota';

type TableItemType = QuotaRoomType & { period: RangeOfDateOnly; quota: string; request: boolean; close: boolean };

const props = defineProps<{
  editedCells: ChessDateCellType[];
  tariffId: string;
}>();

const $display = useDisplay();
const {
  activeQuota,
  setActiveQuota,
  editQuotaReservesCancel,
  editQuotaReservesUpdate,
  isEditQuotaReservesUpdateLoading,
} = useQuotaStore();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();

const dialogRef = ref();
const tableItems = ref<TableItemType[]>([]);
const selectedItems = ref<TableItemType[]>([]);
const isFormDirty = ref(false);

const tableHeaders = [
  {
    key: 'name',
    title: 'Категория номера',
  },
  {
    key: 'room.type.name',
    title: 'Тип',
    sortable: false,
  },
  {
    key: 'period.from',
    title: 'Начальный период',
    sortable: false,
    width: $display.mdAndDown.value ? 124 : 200,
  },
  {
    key: 'period.to',
    title: 'Конечный период',
    sortable: false,
    width: $display.mdAndDown.value ? 124 : 200,
  },
  {
    key: 'quota',
    title: 'Квота',
    sortable: false,
    width: $display.mdAndDown.value ? 124 : 160,
  },
  {
    key: 'request',
    title: 'Под запрос',
    sortable: false,
    width: 108,
  },
  {
    key: 'close',
    title: 'Закрыть',
    sortable: false,
    width: 90,
  },
];

const chessPeriod = computed(() => {
  const firstQuota = activeQuota.value?.roomQuotas[0]?.quotas.at(0);
  const lastQuota = activeQuota.value?.roomQuotas[0]?.quotas.at(-1);

  return { from: firstQuota?.period.from, to: lastQuota?.period.to };
});

const initialPeriod = computed(() => {
  if (props.editedCells.length) {
    return props.editedCells.reduce(
      (result, item) => {
        return {
          from: item.date < result.from ? item.date : result.from,
          to: item.date > result.to ? item.date : result.to,
        };
      },
      { from: props.editedCells[0].date, to: props.editedCells[0].date },
    );
  }

  return chessPeriod.value;
});

const handleDialogOpen = () => {
  if (!activeQuota.value) return;

  const editedCellsIds = props.editedCells.map((item) => item.id);

  const editedRoomQuotas = editedCellsIds.length
    ? activeQuota.value.roomQuotas.filter((item) => editedCellsIds.includes(item.room.cid))
    : activeQuota.value.roomQuotas;

  tableItems.value = editedRoomQuotas.map((item) => {
    return {
      ...item,
      period: {
        from: getDateWithUtcOffset(initialPeriod.value.from, timeZoneOffsetMin.value),
        to: getDateWithUtcOffset(initialPeriod.value.to, timeZoneOffsetMin.value),
      },
      quota: '',
      request: false,
      close: false,
    };
  });

  selectedItems.value = tableItems.value;
};

const handleDialogClose = () => {
  editQuotaReservesCancel();
};

const handleDialogAfterLeave = () => {
  tableItems.value = [];
  selectedItems.value = [];
  isFormDirty.value = false;
};

const handleQuotaUpdate = (item: TableItemType) => {
  item.request = false;
  item.close = false;
};

const handleRequestUpdate = (item: TableItemType) => {
  item.quota = '';
  item.close = false;
};

const handleCloseUpdate = (item: TableItemType) => {
  item.quota = '';
  item.request = false;
};

const handleSave = async () => {
  if (!selectedItems.value.length) {
    dialogRef.value.close();

    return;
  }

  isFormDirty.value = true;

  if (
    tableItems.value.some((item) => selectedItems.value.includes(item) && !+item.quota && !item.request && !item.close)
  ) {
    return;
  }

  const todayWithTimeZoneOffset = getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);

  const result = await editQuotaReservesUpdate({
    tariffScheduleCid: props.tariffId,
    operations: selectedItems.value.map((item) => {
      let operation = QuotaReserveUpdateOperationKind.SetRoomsCount;

      if (item.request) {
        operation = QuotaReserveUpdateOperationKind.SetByRequest;
      }

      if (item.close) {
        operation = QuotaReserveUpdateOperationKind.Close;
      }

      return {
        operation,
        period: {
          from: dayjs(Math.max(+item.period.from, +new Date(todayWithTimeZoneOffset))).format('YYYY-MM-DD'),
          to: dayjs(Math.max(+item.period.to, +new Date(todayWithTimeZoneOffset))).format('YYYY-MM-DD'),
        },
        roomCategoryCid: item.room.cid,
        roomsReserveCount: operation === QuotaReserveUpdateOperationKind.SetRoomsCount ? +item.quota : undefined,
      };
    }),
  });

  if (!result.errors) {
    setActiveQuota(result.data?.updateQuotaReservesUpdate.Quota);
    dialogRef.value.close();
  }
};
</script>
